import { type UIEvent, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "components/button";
import { SearchInput } from "components/input/SearchInput";
import LoadingSpinner from "components/loading/LoadingSpinner";
import { Text } from "components/text";
import { SearchType } from "hooks/search/types";
import { useFilters } from "hooks/search/useFilters";
import { useSearch } from "hooks/search/useSearch";
import {
  type BisacFacetItem,
  groupBisacFacetItems,
  sortBisacItems,
} from "./bisacFilterItems";
import {
  BisacCodeFilterGroups,
  BisacSelectedCodes,
} from "./BisacCodeFilterGroups";
import { BisacFilterSection } from "./BisacFilterSection";
import { BrowseBisacCodesDialog } from "./BrowseBisacCodesDialog";
import { MAX_SELECT_ALL_ITEMS } from "./constants";
import { FilterSectionAction } from "./FilterSectionAction";
import { useBisacFilterAnalytics } from "./hooks/useBisacFilterAnalytics";

// A catalogue-sized list never reaches the DOM at once; scrolling near the bottom reveals the next page.
const PAGE_SIZE = 300;
const SCROLL_THRESHOLD_PX = 240;

interface BisacCodeFilterProps {
  name: string;
  searchQuery?: string;
  searchType?: SearchType;
}

export function BisacCodeFilter({
  name,
  searchQuery = "",
  searchType = SearchType.QUICK,
}: BisacCodeFilterProps) {
  const { register, setValue } = useFormContext();
  const { t } = useTranslation(["books"]);
  const { filters } = useFilters();
  const [browseOpen, setBrowseOpen] = useState(false);
  const searchInput: string | undefined = useWatch({
    name: `filters.${name}.search`,
  });
  const activeFilters = useWatch({ name: "filters" });
  const watchedValues = useWatch({ name: `filters.${name}.values` });
  const selectedValues: string[] = useMemo(
    () => watchedValues ?? [],
    [watchedValues],
  );
  const filterConfig = filters.find((filter) => filter.name === name);
  const searchText = searchInput?.trim() ?? "";
  const isSearch = Boolean(searchText);

  const {
    data: searchData,
    isFetching,
    requestParams,
  } = useSearch(
    {
      query: searchQuery,
      type: searchType,
      facets: [name],
      facetSearch: isSearch ? { field: name, text: searchText } : undefined,
      pageSize: 0,
      filters: activeFilters,
    },
    { debounce: 250 },
  );
  // Until the debounced request carries the typed text, the list on screen answers an older search.
  const isLoadingSearch =
    isFetching || searchText !== (requestParams.aggregation_query ?? "");

  const items = useMemo(
    () =>
      sortBisacItems(
        searchData?.facets?.find((facet) => facet.field === name)?.counts ?? [],
      ),
    [searchData, name],
  );

  const metadataByValue = useMemo(
    () => new Map(items.map((item) => [item.value, item])),
    [items],
  );
  const selectedItems = selectedValues.map(
    (value): BisacFacetItem =>
      metadataByValue.get(value) ?? { value, count: 0, label: value },
  );

  // Selected codes sit in their own block above, so they leave the list below.
  const suggestionItems = items.filter(
    (item) => !selectedValues.includes(item.value),
  );
  const selectAllDisabled =
    suggestionItems.length === 0 ||
    suggestionItems.length > MAX_SELECT_ALL_ITEMS;

  const codeCounts = new Map(
    groupBisacFacetItems(suggestionItems).map((subject) => [
      subject.code,
      subject.items.length,
    ]),
  );

  const [page, setPage] = useState({ searchText, limit: PAGE_SIZE });
  const limit = page.searchText === searchText ? page.limit : PAGE_SIZE;
  const visibleItems = suggestionItems.slice(0, limit);
  const hasMore = visibleItems.length < suggestionItems.length;

  const analytics = useBisacFilterAnalytics({
    selectedCount: selectedValues.length,
    searchText,
    isListPending: isLoadingSearch,
    items,
  });

  const handleListScroll = (event: UIEvent<HTMLDivElement>) => {
    const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;

    if (
      hasMore &&
      scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD_PX
    ) {
      setPage({ searchText, limit: limit + PAGE_SIZE });
    }
  };

  const setSelectedValues = (values: string[]) =>
    setValue(`filters.${name}.values`, values);

  const handleToggle = (value: string, checked: boolean) => {
    setSelectedValues(
      checked
        ? Array.from(new Set([...selectedValues, value]))
        : selectedValues.filter((selectedValue) => selectedValue !== value),
    );
    analytics.logCodeToggled(value, checked);
  };

  const handleSelectAll = () => {
    if (selectAllDisabled) {
      return;
    }

    setSelectedValues(
      Array.from(
        new Set([
          ...selectedValues,
          ...suggestionItems.map((item) => item.value),
        ]),
      ),
    );
    analytics.logBulkSelection("select_all", suggestionItems.length);
  };

  const handleDeselectAll = () => {
    analytics.logBulkSelection("deselect_all", selectedValues.length);
    setSelectedValues([]);
  };

  const openBrowse = () => {
    analytics.logBrowseOpened();
    setBrowseOpen(true);
  };

  const listProps = { name, selectedValues, onToggle: handleToggle };

  const renderSuggestions = () => {
    if (isLoadingSearch) {
      return (
        <div className="flex w-full justify-center py-2 px-6 text-sm">
          <LoadingSpinner size="sm" />
        </div>
      );
    }

    if (!suggestionItems.length) {
      return (
        <Text
          as="div"
          variant="body"
          className="flex w-full justify-center text-center py-2 px-6"
        >
          {t("books:filters.multiSelect.noSuggestionsFound")}
        </Text>
      );
    }

    return (
      <div
        className="relative max-h-[340px] overflow-y-auto"
        onScroll={handleListScroll}
      >
        <BisacCodeFilterGroups
          subjects={groupBisacFacetItems(visibleItems)}
          codeCounts={codeCounts}
          {...listProps}
        />
        {hasMore && !isSearch && (
          <Text
            as="div"
            variant="muted"
            className="px-6 py-2.5 text-center text-xs"
          >
            {t("books:filters.bisacCodes.showing", {
              shown: visibleItems.length,
              total: suggestionItems.length,
            })}
          </Text>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-y-2">
      <div className="px-6">
        <Button
          variant="link"
          icon="search"
          className="p-0 text-primary-600 hover:text-primary-700"
          onClick={openBrowse}
        >
          {t("books:filters.bisacCodes.browse")}
        </Button>
      </div>
      <SearchInput
        className="mx-6 mb-2 w-[calc(100%-3rem)]"
        placeholder={filterConfig?.placeholder}
        {...register(`filters.${name}.search`)}
      />
      {selectedValues.length > 0 && (
        <BisacFilterSection
          label={t("books:filters.multiSelect.selected")}
          testId="bisac-filter-selected"
          action={
            <FilterSectionAction
              label={t("books:filters.multiSelect.deselectAll")}
              onClick={handleDeselectAll}
            />
          }
        >
          <div className="max-h-[180px] overflow-y-auto">
            <BisacSelectedCodes items={selectedItems} {...listProps} />
          </div>
        </BisacFilterSection>
      )}
      <BisacFilterSection
        label={t("books:filters.multiSelect.suggestions")}
        testId="bisac-filter-suggestions"
        action={
          <FilterSectionAction
            label={t("books:filters.multiSelect.selectAll")}
            disabled={selectAllDisabled}
            disabledTooltip={
              suggestionItems.length > MAX_SELECT_ALL_ITEMS
                ? t("books:filters.multiSelect.selectAllDisabled")
                : undefined
            }
            onClick={handleSelectAll}
          />
        }
      >
        {renderSuggestions()}
      </BisacFilterSection>
      <BrowseBisacCodesDialog open={browseOpen} onOpenChange={setBrowseOpen} />
    </div>
  );
}
